<script>
  import Section from '../components/Section.svelte';
  import Alert from '../components/Alert.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import AggregateBuilder from '../components/AggregateBuilder.svelte';
  import { getCubeAuthoring, saveAggregate, previewAggregates } from '../lib/api.js';
  import { go, nav } from '../lib/nav.svelte.js';
  import { HELP } from '../lib/help.js';

  // A cube a comparison needs and no cube counts yet. It counts the terms of
  // one dataset (docs/ref-belonging-plan.md, step 7.5), whichever of its
  // sources fills them, so it starts from a dataset.
  const authoring = getCubeAuthoring();
  let datasetId = $state(nav.params.dataset ?? '');

  // The dataset's columns: those of its record classes, and what every
  // record has (the period).
  function datasetMapping(mapping, dataset) {
    const classes = new Set(dataset.recordClasses);
    return { ...mapping,
      records: Object.fromEntries(Object.entries(mapping.records).filter(([c]) => classes.has(c))),
      columns: mapping.columns.filter((c) => c.record === 'shared' || classes.has(c.record)) };
  }

  // Saved and computed: back to the dataset, where it now is.
  async function mint(aggregate) {
    await saveAggregate({ aggregate });
    go('data/dataset', { id: datasetId, tab: 'sharing' });
  }
  // There are no sample rows here, so the counts come from the private
  // records the Pod already holds. Reads only.
  const look = (aggregate) => previewAggregates({ aggregate });
</script>

<HelpDrawer {...HELP.cubeNew} />

<h1 class="wf2-title wf2-title-level-1">New cube</h1>
<p class="wf2-subtitle">Count a breakdown of a dataset its cubes do not have yet — it stays private until a manager shares it</p>

<Section>
  <Async promise={authoring} loading="Reading your datasets…">
    {#snippet children(a)}
      {#if a.resolved.length}
        <label class="wf2-field">Dataset
          <select class="wf2-control" bind:value={datasetId}>
            <option value="">Choose a dataset…</option>
            {#each a.resolved as d (d.id)}<option value={d.id}>{d.label}</option>{/each}
          </select>
        </label>
        {@const dataset = a.resolved.find((d) => d.id === datasetId)}
        {#if dataset}
          {#key dataset.id}
            <AggregateBuilder
              mapping={datasetMapping(a.mapping, dataset)} aggregateMapping={a.mapping} {dataset} aggregations={a.aggregations}
              datasets={a.datasets}
              onsave={mint}
              onpreview={look}
              saveLabel="Save and compute private cube" />
          {/key}
        {:else}
          <Alert title="Choose a dataset" variant="info"><p>A cube counts the terms of one dataset.</p></Alert>
        {/if}
      {:else}
        <Alert title="No dataset yet" variant="info"><p>Make a dataset from a file before building cubes.</p></Alert>
      {/if}
    {/snippet}
  </Async>
</Section>
